import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  Button,
  Container,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import useIsSmallScreen from "../../Payment Type/Component/small screen/SmallScreen";
import useTableSearch from "../../../hooks/useTableSearch";
import TableSearch from "../../../Components/Common/TableSearch";
import TaxService from "../../../services/OrderingServices/TaxService";
import TaxTypeForm from "../Component/TaxTypeForm";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const Taxtype = () => {
  const [taxTypeList, setTaxTypeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formModal, setFormModal] = useState(false);
  const [selectedTaxType, setSelectedTaxType] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const isSmallScreen = useIsSmallScreen();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const fetchTaxTypes = async () => {
    try {
      setLoading(true);
      const response = await TaxService.getList(true); // include inactive
      setTaxTypeList(response || []);
    } catch (error) {
      console.error("Error fetching tax types:", error);
      toast.error("Failed to load tax types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTaxTypes();
  }, []);

  const { searchTerm, setSearchTerm, filteredData } = useTableSearch(taxTypeList, [
    "name",
    "description",
    "rate",
    "calculationType",
  ]);

  const toggleFormModal = () => {
    setFormModal(!formModal);
    if (formModal) setSelectedTaxType(null);
  };

  const handleEdit = (row) => {
    setSelectedTaxType(row);
    setFormModal(true);
  };

  const handleDeleteClick = (id) => {
    setSelectedTaxType(id);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (selectedTaxType) {
      try {
        await TaxService.delete(selectedTaxType);
        toast.success("Tax type deleted successfully");
        fetchTaxTypes();
      } catch (error) {
        toast.error("Failed to delete tax type");
      } finally {
        setDeleteModal(false);
        setSelectedTaxType(null);
      }
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * perPage + index + 1,
      width: "75px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Rate",
      selector: (row) => row.rate,
      sortable: true,
      cell: (row) =>
        row.calculationType === "Percentage" ? `${row.rate}%` : row.rate,
    },
    {
      name: "Description",
      selector: (row) => row.description || "N/A",
      sortable: true,
    },
    {
      name: "Calculation Type",
      selector: (row) => row.calculationType,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => (row.isActive ? "Active" : "Inactive"),
      cell: (row) => (
        <span className={`badge bg-${row.isActive ? "success" : "danger"}`}>
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex gap-1">
          {!row.isSystemGenerated && (
            <>
              <Button
                color="btn btn-soft-warning"
                className="btn-sm"
                onClick={() => handleEdit(row)}
              >
                <i className="ri-edit-line" />
              </Button>
              <Button
                color="btn btn-soft-danger"
                className="btn-sm"
                onClick={() => handleDeleteClick(row.id)}
              >
                <i className="ri-delete-bin-5-line" />
              </Button>
            </>
          )}
          {row.isSystemGenerated && (
            <span className="text-muted small italic">System Default</span>
          )}
        </div>
      ),
      center: true,
    },
  ];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Tax Type" pageTitle="System Setup" />

        <div
          className="p-3 p-lg-4 mt-1"
          style={{
            background: "#fff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          <Row className="justify-content-between align-items-center mb-3">
            <Col md={6}>
              <h5 className="mb-0 fw-bold text-dark">Tax Types List</h5>
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-md-end align-items-center gap-3">
                <TableSearch
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search by name, rate or desc..."
                />
                <Button
                  color="success"
                  onClick={() => setFormModal(true)}
                  className="d-flex align-items-center gap-2"
                  style={{ borderRadius: "8px" }}
                >
                  <i className="bx bx-plus"></i> Add Tax
                </Button>
              </div>
            </Col>
          </Row>

          {isSmallScreen ? (
            loading ? (
              <div className="d-flex justify-content-center my-5">
                <Triangle visible={true} height="80" width="80" color="#5B71B9" />
              </div>
            ) : (
              <Row>
                {filteredData?.map((item, index) => (
                  <Col md={12} key={item.id || index} className="mb-3">
                    <Card className="shadow-sm border">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="fw-bold mb-1">{item.name}</h6>
                            <p className="text-muted mb-1 small">{item.description}</p>
                            <span className="badge bg-primary me-2">
                              {item.rate}
                              {item.calculationType === "Percentage" ? "%" : ""}
                            </span>
                            <span className="badge bg-info me-2">{item.calculationType}</span>
                            <span className={`badge bg-${item.isActive ? "success" : "danger"}`}>
                              {item.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                          {!item.isSystemGenerated && (
                            <Dropdown
                              isOpen={item.dropdownOpen}
                              toggle={() => {
                                setTaxTypeList((prev) =>
                                  prev.map((t) =>
                                    t.id === item.id
                                      ? { ...t, dropdownOpen: !t.dropdownOpen }
                                      : { ...t, dropdownOpen: false }
                                  )
                                );
                              }}
                            >
                              <DropdownToggle color="light" className="btn-sm rounded-circle shadow-none border">
                                <i className="ri-more-2-fill"></i>
                              </DropdownToggle>
                              <DropdownMenu end>
                                <DropdownItem onClick={() => handleEdit(item)}>
                                  <i className="ri-edit-line text-warning me-2"></i> Edit
                                </DropdownItem>
                                <DropdownItem onClick={() => handleDeleteClick(item.id)}>
                                  <i className="ri-delete-bin-5-line text-danger me-2"></i> Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            )
          ) : (
            <CustomDataTable
              responsive
              striped
              pagination
              fixedHeader
              persistTableHead
              progressPending={loading}
              customStyles={customStyles}
              onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
              onChangePage={(page) => setCurrentPage(page)}
              paginationDefaultPage={currentPage}
              paginationPerPage={perPage}
              progressComponent={
                <div className="my-5 text-center w-100">
                  <Triangle visible={true} height="80" width="80" color="#5B71B9" />
                  <h5 className="mt-2 text-muted">Loading Tax Types...</h5>
                </div>
              }
              columns={columns}
              data={filteredData}
              noDataComponent={
                <div className="text-center my-5 w-100 py-5">
                  <i className="ri-emotion-unhappy-line display-4 text-secondary opacity-50" />
                  <h5 className="fw-semibold text-secondary mt-3">No Tax Types Found</h5>
                  <p className="text-muted mb-0">Try adjusting your search or add a new tax type.</p>
                </div>
              }
            />
          )}
        </div>
      </div>

      <TaxTypeForm
        modal={formModal}
        toggleModal={toggleFormModal}
        selectedTaxType={selectedTaxType}
        fetchTaxTypes={fetchTaxTypes}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        selectedItem={selectedTaxType}
      />
    </Container>
  );
};

export default Taxtype;